// Advanced Mode data (Phase 1).
// Canonical source: design/canonical-numbers.md section 16.
// Annual demand/generation is TWh; peak demand/firm capacity is GW.
// The magnitudes are readable game numbers, not real-world plant
// ratings — capacityUnits and firmCapacityCredit are not capacity
// factors.

import { PLANTS } from './plants.js';
import { RESILIENCE_AWARD_TIERS } from './awards.js';
import { EOS_DUPLICATE_MULTIPLIER } from './constants.js';

export const ADVANCED_UNITS = {
  energy: 'TWh',
  capacity: 'GW'
};

// Section 16.1: annual energy demand and peak demand per year.
export const ADVANCED_DEMAND_BY_YEAR = {
  1: { energy: 12, peak: 4 },
  2: { energy: 25, peak: 8 },
  3: { energy: 45, peak: 14 }
};

export const RESERVE_MARGIN = 1.1;

// Section 16.4: peak capacity shortfall penalty schedule.
export const PEAK_SHORTFALL_TIER1_LIMIT_UNITS = 2;
export const PEAK_SHORTFALL_TIER1_RATE_PER_UNIT = 5;
export const PEAK_SHORTFALL_TIER2_RATE_PER_UNIT = 12;

// Section 16.9: statistical firming. Each distinct variable family
// beyond the first raises the firm capacity credit of every variable
// plant, capped. Uncorrelated variable resources firm each other.
export const VARIABLE_DIVERSITY_UPLIFT_PER_FAMILY = 0.075;
export const VARIABLE_DIVERSITY_UPLIFT_CAP = 0.225;

// Declining marginal firm capacity WITHIN a variable family.
//
// Entry i is the share of firm capacity credited to the (i+1)th plant
// of a family; the last entry is the floor for every further copy.
// Reliable plants and storage are never affected.
//
// Per FAMILY rather than per plant id, because the physics is about
// the resource: Solar PV and Concentrated Solar saturate the same
// daylight peak, so a second panel farm firms the evening peak no
// better than the first. It composes with the diversity uplift above
// into one story - spread your variables across resources, do not
// stack one.
//
// Applied biggest-unit-first within each family, so the schedule
// prices the MARGINAL copies and the result cannot depend on the order
// a player happened to build in.
//
// The accepted schedule gives full credit to the first plant in a
// family, 75% to the second, and 50% to every later plant. Historical
// calibration at section 4.23 found steeper schedules made Year 3
// adequacy too weak before plans were re-optimised.
export const VARIABLE_FIRM_DECLINE_SCHEDULE = [1, 0.75, 0.5];

// Historical v0.5 award constants retained for calibration re-scoring
// and pure helper tests. Advanced v1.0 live scoring imports none of
// these award settings and sets every compatibility award field to zero.
export const ADVANCED_AWARD_MULTIPLIER = 1.75;

// Historical v0.5 resilience tiers.
export const ADVANCED_RESILIENCE_AWARD_TIERS = [
  {
    minFamilies: 7,
    maxLargestFamilyShareExclusive: 0.35,
    bonus: 12,
    requiresFossilFreePortfolio: true,
    label: 'Maximum resilience'
  },
  ...RESILIENCE_AWARD_TIERS
];

// Section 16.10: escalating carbon price. Advanced Mode weights each
// year's emissions before the Classic bracket lookup; the bracket
// table itself is unchanged. The Year 3 weight is deliberately steep:
// with the pooled whole-game bracket, a mild weight let a clean
// portfolio absorb fresh Year 3 coal inside its emissions headroom.
export const ADVANCED_CARBON_YEAR_WEIGHTS = { 1: 0.5, 2: 1, 3: 2.5 };

// Section 16.14: Advanced Mode economies of scale.
//
// Deliberately held at Classic parity. The per-mode wiring exists so
// this CAN diverge without touching frozen Classic scoring, but a
// measured attempt at x0.8 was reverted: it did not reduce
// duplication (all 10 top frontier plans still duplicated something,
// 5 of 10 still duplicated Geothermal) while it did break the
// archetype spread, taking Fossil Heavy down to 1.9%. Duplication is
// driven by capital efficiency rather than by this discount - about
// a dozen plants are worth building for twelve slots, and the best
// few beat the next few by more than the historical resilience award
// paid for breadth. Advanced v1.0 has no post-game awards. Its live
// stacking controls are the first-copy opex rule, declining variable
// firm credit, and resource-development premium. Numbers are in
// canonical-numbers sections 16.8.1, 16.9, and 16.14.
export const ADVANCED_EOS_DUPLICATE_MULTIPLIER = EOS_DUPLICATE_MULTIPLIER;

// Advanced-only: the first copy of a plant pays FULL opex, and only
// each additional copy gets the duplicate discount.
//
// The rule everywhere else - and the one Classic is frozen on - gives
// the discount to every copy once you own two, so a second Geothermal
// halves the opex of the first one as well. That is the mechanism
// behind the duplication crutch section 16.12 has carried unaddressed
// since v0.4: at the v1.0 frontier all ten top plans duplicate
// something, 39 extra copies across the top ten at both seeds, and the
// spare-slot decision in Renewable Plus Storage was worth $3.89M of
// which the WHOLE gain was this discount (§4.17).
//
// Cutting the multiplier instead was tried at v0.4 and reverted: it did
// not reduce duplication and it broke the archetype spread (§16.14).
// This rule targets the specific thing being exploited rather than
// making duplication uniformly dearer.
//
// A diminishing schedule (2nd copy 50%, 3rd 25%, ...) was considered
// and rejected for a first cut: more knobs, harder attribution, and the
// n-1 rule already removes the exploit.
export const ADVANCED_EOS_FIRST_COPY_PAYS_FULL = true;

// Section 16.8.1: exact-technology resource development. The first
// three player-built copies use listed capital. Every later copy pays
// this one-time premium when built. Inherited plants are outside the
// build palette and never enter the lifetime count.
export const RESOURCE_DEVELOPMENT_PREMIUM_STARTS_AT_COPY = 4;
export const RESOURCE_DEVELOPMENT_PREMIUM_PER_BUILD = 1.5;

// Section 16.11: plant retirement. Flat decommissioning fee per
// plant retired, paid in the year of retirement. Capital is sunk;
// the grid connection slot is freed for the same build phase.
export const DECOMMISSION_FEE_PER_PLANT = 1;

// Candidate G-R adds this flat amount only when the inherited coal
// plant is retired in Year 1. It is separate from the general
// early-closure curve below, which remains unchanged.
export const YEAR_ONE_INHERITED_COAL_RETIREMENT_ADDITION = 1.5;

// Section 16.11: early-closure charge. Closing a plant ahead of time
// costs more than closing it on schedule - contracts broken at short
// notice, an unplanned site clearance, a workforce with nowhere to go.
// The charge is added to the flat fee above and priced off the plant's
// roster capital, so a big plant is dearer to close early than a small
// one.
//
// This is the lever that makes the legacy coal worth holding through
// Year 1. Calibration §4.9 established why the obvious levers cannot:
// retirement resolves BEFORE the year runs, so a plant retired in
// Year 1 never generates, never emits and never pays opex - the
// decommissioning fee is the only cost an early exit actually pays.
//
// The capital scaling is load-bearing, not decoration. A flat charge
// would push the diesel to Year 2 before it pushed the coal, because
// the coal's Year 1 exit is worth more to avoid. Scaling by roster
// capital puts three times the pressure on the coal ($3M) as on the
// diesel ($1M), which is the ordering the derate was designed to
// create. Measured at §4.10: 1.5 passes both acceptance checks at two
// seeds, 1.0 fails at seed 2, 3.0 overshoots and starts holding the
// diesel.
export const EARLY_CLOSURE_SURCHARGE = 1.5;
export const EARLY_CLOSURE_YEAR_WEIGHTS = { 1: 1, 2: 0.5, 3: 0 };

// Historical v0.5 Affordability Award, retained for the calibration
// tools which compare rejected candidate rules.
//
// The award exists to DECORRELATE the credits. The clean award, the
// resilience award and the carbon bonus all reward the same portfolio
// shape - clean, diverse, clear of the inherited fleet - so one shape
// collected all $44.4M of them and won 87.7% of the archetype match
// (§4.17, §4.18). Affordability pulls the other way: it pays for
// delivering the energy cheaply, which is what few large plants do and
// what a broad diverse fleet does not.
//
// Metric: (total capital + total opex) per TWh actually delivered,
// where delivered is each year's energy demand less that year's
// energy shortfall - i.e. min(demand, generation), summed. Not per TWh
// GENERATED: surplus nobody needs would improve that ratio, so it
// would pay for overbuilding. Tiered rather than linear because total
// cost already counts capital and opex, so a linear reward would
// double-count and simply amplify whatever is cheapest (§4.20).
//
// Decommissioning fees are deliberately NOT in the numerator. Putting
// them there would penalise clearing the inherited fleet, which is the
// arc the mode is built to teach.
export const ADVANCED_AFFORDABILITY_AWARD_TIERS = [
  {
    maxCostPerEnergyDelivered: 0.95,
    bonus: 12,
    label: 'Lowest-cost system'
  },
  {
    maxCostPerEnergyDelivered: 1.05,
    bonus: 8,
    label: 'Low-cost system'
  },
  {
    maxCostPerEnergyDelivered: 1.15,
    bonus: 5,
    label: 'Competitive system'
  }
];

// The gates, both load-bearing and both measured at §4.20.
//
// Peak: an affordability award that paid for skimping would teach the
// opposite of the intended lesson, so a system that missed its peak in
// any year wins nothing. Measured, this gate also subsumes an energy
// shortfall gate - the two scored identically to the cent - and it is
// the one that leaves the per-TWh denominator meaningful, because
// under an energy-shortfall gate delivered energy is always exactly
// demand and the metric collapses into "capital plus opex".
//
// Carbon: the cheapest way to deliver a TWh is fossil, so without a
// carbon floor this award pays Fossil Heavy more than anyone (it is
// the cheapest producer on the board) and takes it to 11% of the
// match. The floor is a BRACKET floor, not a clean-portfolio gate:
// setting it at "must be in a bonus bracket" excludes every strategy
// that could otherwise reach a tier and pays nobody at all, which is
// the collinearity this award exists to remove.
//
// Set at -5, i.e. weighted emissions of 30 or below. A floor of -10
// was implemented first and FAILED acceptance check 1 at seed 2
// (§4.21): the cheapest capital on the board is the inherited coal a
// player was given for nothing, so at -10 the award pays for sitting
// on it, and a real frontier search found a plan that keeps the coal
// and wins by $0.28M. Lifting the floor one bracket excludes exactly
// those plans - a system still running the inherited fleet in Year 3
// lands above 30 weighted - while leaving every measured
// fleet-clearing plan untouched.
//
// The cost of the tighter floor is recorded rather than hidden: it
// also gates out Nuclear Firm and Gas Backup, so the archetype spread
// returns to its no-award value. Those two are cheap BECAUSE they
// keep the inherited coal, so paying them and paying the check 1
// failure are the same mechanism, and it cannot be had one way only
// (§4.21).
export const ADVANCED_AFFORDABILITY_MIN_CARBON_BRACKET_BASE = -5;

// A player claims their best two of Clean / Resilience /
// Affordability - the energy trilemma as a scoring rule. Without the
// cap a portfolio that is clean, diverse AND cheap collects all three
// and runs away with the frontier (§4.20: it overtakes the champion by
// $2.01M at seed 2, about three standard errors). Classic keeps two
// awards and no cap.
export const ADVANCED_AWARDS_COUNTED = 2;

// Section 16.13: the Just Transition bonus is REMOVED at v1.0.
// It paid $5M x 1.75 for retiring a Fossil Thermal plant and ending
// fossil-free, and was farmable for $6.74M: build the cheapest fossil
// plant on the board, run it one year, scrap it. The freed-slot refund
// made the replacement free, so the "forfeited final-year firm
// capacity" its sizing assumed was never forfeited (v0.5 baseline §2).
//
// The early-closure charge above replaces it and cannot be farmed:
// the bonus paid for the FACT of retiring, the charge prices the
// TIMING of retiring, which is what the mode is teaching. Calibration
// §4.11 has the decision and its accepted cost (a heavier never-retire
// tail in the lower ranks; the winner still retires the coal).

// Section 16.3: per-plant capacity data. Firm capacity is fixed
// ex ante (capacityUnits * firmCapacityCredit), then modified by
// disasters; it never depends on the annual output roll.
export const ADVANCED_PLANT_CAPACITY = {
  'diesel-generator': { capacityUnits: 2, firmCapacityCredit: 0.95 },
  'combined-cycle-gas': { capacityUnits: 3, firmCapacityCredit: 0.95 },
  'coal-plant': { capacityUnits: 3, firmCapacityCredit: 0.9 },
  'carbon-capture-coal': { capacityUnits: 3, firmCapacityCredit: 0.85 },
  geothermal: { capacityUnits: 2, firmCapacityCredit: 0.9 },
  'small-modular-nuclear': { capacityUnits: 2, firmCapacityCredit: 0.9 },
  // Large Nuclear carries a slightly lower credit than SMR: losing
  // one large unit is a bigger single-point risk at peak.
  'large-nuclear': { capacityUnits: 4, firmCapacityCredit: 0.85 },
  'large-hydro-dam': { capacityUnits: 4, firmCapacityCredit: 0.8 },
  hydrogen: { capacityUnits: 3, firmCapacityCredit: 0.9 },
  'solar-pv': { capacityUnits: 2, firmCapacityCredit: 0.15 },
  'concentrated-solar': { capacityUnits: 2, firmCapacityCredit: 0.45 },
  'onshore-wind': { capacityUnits: 2, firmCapacityCredit: 0.2 },
  'offshore-wind': { capacityUnits: 3, firmCapacityCredit: 0.3 },
  tidal: { capacityUnits: 2, firmCapacityCredit: 0.5 },
  'wave-power': { capacityUnits: 2, firmCapacityCredit: 0.3 },
  'run-of-river-hydro': { capacityUnits: 2, firmCapacityCredit: 0.4 }
};

export const STORAGE_FAMILY = 'storage';
export const PLANT_TYPE_STORAGE = 'storage';

// Section 16.5: storage technologies. Storage adds no annual
// generation and only ever answers the peak capacity layer; firm
// contribution is conditional on the expected-surplus charging rule
// implemented in src/engine/advancedCapacity.js. Long Duration
// Storage additionally carries its charge into the next year
// (section 16.5.2); Grid Battery starts every year empty.
//
// roundTripEfficiency means two different things by design: a
// same-year store pays the loss at charge, a carrier pays it when
// charge crosses a year. Compare the two on storageSurplusToFill,
// never on this field.
export const STORAGE_PLANTS = [
  {
    id: 'grid-battery',
    name: 'Grid Battery',
    family: STORAGE_FAMILY,
    type: PLANT_TYPE_STORAGE,
    capital: 5,
    opex: 1,
    averageOutput: 0,
    capacityUnits: 3,
    storageEnergyUnits: 3,
    firmCapacityCredit: 0.9,
    roundTripEfficiency: 0.85,
    carriesEnergyBetweenYears: false,
    emissions: 0,
    role: 'Conditional firm capacity from stored surplus',
    imageSrc: '/plant-images/Battery Grid.webp'
  },
  {
    id: 'long-duration-storage',
    name: 'Long Duration Storage',
    family: STORAGE_FAMILY,
    type: PLANT_TYPE_STORAGE,
    // Rebalanced for v0.4 (capital 8 -> 6, credit 0.6 -> 0.8): at the
    // old stat line LDS was strictly dominated by Grid Battery in
    // every measured portfolio.
    //
    // Repriced (capital 6 -> 4, opex 1.5 -> 1) alongside the
    // charge-carrying model. LDS no longer banks energy against a
    // future shortfall; it carries charge, which buys peak capacity
    // only. That is a narrower payoff, and it charges last in the
    // priority queue, so the old premium over Grid Battery made it a
    // trap.
    //
    // Calibrated 2026-07-28 (section 16.5.3): capacityUnits 2 -> 3,
    // capital back to $5M. At 2 units it appeared in zero of the top
    // ten frontier plans and cost +$3.68M in Grid Battery's slot.
    // The constraint was its GW ceiling, not its 6 TWh charge
    // requirement - it fills to 100% in both surplus-rich and
    // firm-tight fleets, so cutting the requirement moved nothing.
    // Priced at $5M rather than $4M so it stays marginally behind
    // Grid Battery within a single year (2.4 GW against 2.7 GW for
    // the same money) and the carry is what you are buying.
    capital: 5,
    opex: 1,
    averageOutput: 0,
    capacityUnits: 3,
    storageEnergyUnits: 6,
    firmCapacityCredit: 0.8,
    roundTripEfficiency: 0.7,
    carriesEnergyBetweenYears: true,
    emissions: 0,
    role: 'Carries its charge into the next year',
    imageSrc: '/plant-images/Long duration.webp'
  }
];

// The buildable palette: Classic plants merged with capacity data,
// plus storage. Classic PLANTS is never mutated. The legacy fleet
// below is deliberately NOT in this list - those plants are owned
// from the start and must never be offered on the build screen.
export const ADVANCED_PLANTS = [
  ...PLANTS.map((p) => ({ ...p, ...ADVANCED_PLANT_CAPACITY[p.id] })),
  ...STORAGE_PLANTS
];

const BUILDABLE_BY_ID = Object.fromEntries(
  ADVANCED_PLANTS.map((p) => [p.id, p])
);

// Section 16.15: the accepted legacy fleet derates. The inherited
// fleet leaves a gap one suitable firm build can close, so Year 1
// opens with a repair decision instead of an immediate crisis.
export const LEGACY_DERATE_FACTORS = {
  'coal-plant': 0.75,
  'diesel-generator': 0.6
};

// The derate is a permanent nameplate reduction, not a bad year, so
// it applies to annual generation and firm capacity at the SAME
// factor. It is modelled by scaling capacityUnits rather than the
// firm credit, which leaves the credit meaning what it means
// everywhere else.
//
// Emissions and opex are deliberately NOT derated. An old unit is
// less efficient - it burns more fuel per unit of output - so the
// legacy plants emit and cost their full showroom figures while
// producing less. That is what gives the derate its teeth: the coal
// goes from 1.00 to 1.33 emissions per TWh, the diesel from 1.50 to
// 2.50, which is what makes the diesel the obvious first exit and
// the coal the genuine dilemma.
//
// These are separate plant TYPES rather than derated instances of the
// showroom plants. A portfolio is an array of plant-type ids, so a
// per-instance derate would mean rewriting every aggregation in the
// engine; distinct ids give the same behaviour for free, and mean a
// Coal Plant built in Year 2 is new and runs at full spec.
function makeLegacyPlant(sourceId) {
  const source = BUILDABLE_BY_ID[sourceId];
  const factor = LEGACY_DERATE_FACTORS[sourceId];
  return {
    ...source,
    id: `legacy-${sourceId}`,
    name: `Legacy ${source.name}`,
    legacy: true,
    legacySourceId: sourceId,
    legacyDerateFactor: factor,
    // Capital is sunk before the game begins and never enters the
    // player's total cost. The showroom figure is kept as
    // rosterCapital because the early-closure charge is priced off
    // what the plant is worth, not off what this player paid - a fee
    // the inherited fleet escapes for free is backwards.
    capital: 0,
    rosterCapital: source.capital,
    output: source.output * factor,
    averageOutput: source.averageOutput * factor,
    capacityUnits: source.capacityUnits * factor,
    role: `Inherited, running at ${Math.round(factor * 100)}% of nameplate`,
    // PlantTile falls back to /plant-images/<id>.webp, which does not
    // exist for these ids, so the source plant's image is resolved
    // explicitly here.
    imageSrc: source.imageSrc ?? `/plant-images/${sourceId}.webp`
  };
}

export const ADVANCED_LEGACY_PLANTS = Object.keys(LEGACY_DERATE_FACTORS).map(
  makeLegacyPlant
);

// Section 16.15: Advanced Mode begins with these already on the grid.
// Order is fixed and the setup consumes no randomness - paired-seed
// comparison is the whole balance methodology, so nothing here may
// touch the RNG stream.
export const ADVANCED_STARTING_PORTFOLIO = ADVANCED_LEGACY_PLANTS.map(
  (p) => p.id
);

// Lookup covers the legacy fleet; the palette above does not.
export const ADVANCED_PLANT_BY_ID = Object.fromEntries(
  [...ADVANCED_PLANTS, ...ADVANCED_LEGACY_PLANTS].map((p) => [p.id, p])
);

export const STORAGE_PLANT_IDS = STORAGE_PLANTS.map((p) => p.id);

// Section 16.15: Advanced Mode grid connection slots. Year 1 gains a
// fourth slot because the legacy fleet occupies two of them from the
// start; net usable capacity across the game falls from 12 to 11.
// src/data/slots.js is shared with Classic and must not move.
export const ADVANCED_SLOTS_NEW_PER_YEAR = { 1: 4, 2: 4, 3: 5 };
export const ADVANCED_SLOTS_CUMULATIVE_BY_YEAR = { 1: 4, 2: 8, 3: 13 };
